import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";

const isPlaceholderOption = (option) =>
    Boolean(option) && option.value === "" && /select/i.test(option.label);

const PillIndicator = ({ option, showDot = true, showAvatar = false }) => {
    if (isPlaceholderOption(option)) {
        return null;
    }

    if (showAvatar) {
        return (
            <span
                className={`clk-mk-pill-avatar${option.value ? "" : " is-empty"}`}
                style={{ background: option.color }}
            >
                {option.value || "-"}
            </span>
        );
    }

    if (showDot) {
        return <i style={{ background: option.color }} />;
    }

    return null;
};

const PillSelect = ({ value, options, onChange, ariaLabel, showDot = true, showAvatar = false }) => {
    const [open, setOpen] = useState(false);
    const [menuRect, setMenuRect] = useState(null);
    const rootRef = useRef(null);
    const menuRef = useRef(null);
    const selected = options.find((o) => o.value === value) || options[0];
    const menuOptions = options.filter((opt) => !isPlaceholderOption(opt));
    const showSelectedIndicator = (indicator) =>
        indicator && !isPlaceholderOption(selected);

    const updateMenuRect = () => {
        if (!rootRef.current) return;
        const rect = rootRef.current.getBoundingClientRect();
        setMenuRect({
            top: rect.bottom + 4,
            left: rect.left,
            width: Math.max(rect.width, 160),
        });
    };

    useLayoutEffect(() => {
        if (!open) {
            setMenuRect(null);
            return undefined;
        }

        updateMenuRect();
        window.addEventListener("scroll", updateMenuRect, true);
        window.addEventListener("resize", updateMenuRect);

        return () => {
            window.removeEventListener("scroll", updateMenuRect, true);
            window.removeEventListener("resize", updateMenuRect);
        };
    }, [open]);

    useEffect(() => {
        const onPointerDown = (e) => {
            if (rootRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, []);

    const handleSelect = (next) => {
        onChange(next);
        setOpen(false);
    };

    if (!selected) return null;

    const menu = open && menuRect
        ? createPortal(
            <ul
                ref={menuRef}
                className="clk-mk-pill-menu clk-mk-pill-menu--portal"
                role="listbox"
                aria-label={ariaLabel}
                style={{
                    top: menuRect.top,
                    left: menuRect.left,
                    minWidth: menuRect.width,
                }}
            >
                {menuOptions.map((opt) => (
                    <li key={`${opt.value}-${opt.label}`}>
                        <button
                            type="button"
                            role="option"
                            aria-selected={value === opt.value}
                            className={value === opt.value ? "is-active" : ""}
                            onClick={() => handleSelect(opt.value)}
                        >
                            <PillIndicator option={opt} showDot={!showAvatar} showAvatar={showAvatar} />
                            <span>{opt.label}</span>
                        </button>
                    </li>
                ))}
            </ul>,
            document.body
        )
        : null;

    return (
        <div className="clk-mk-pill-dropdown" ref={rootRef}>
            <button
                type="button"
                className={`clk-mk-pill-select${open ? " is-open" : ""}`}
                onClick={() => setOpen((v) => !v)}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <PillIndicator
                    option={selected}
                    showDot={showSelectedIndicator(showDot)}
                    showAvatar={showSelectedIndicator(showAvatar)}
                />
                <span className="clk-mk-pill-select-label">{selected.label}</span>
                <Icon name="chevron-down" size={12} className={`clk-mk-pill-chevron${open ? " is-open" : ""}`} />
            </button>
            {menu}
        </div>
    );
};

export default PillSelect;
